import React, { useState, useEffect, useCallback } from 'react';
import { claimApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  DollarSign, FileText, Clock, TrendingUp, ArrowRight,
  CheckCircle, XCircle, AlertTriangle, RefreshCw, ChevronRight,
  ArrowLeft, Activity, Zap, Eye, Send, Shield, RotateCcw,
  Database, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import DenialPredictionDialog from '../../components/DenialPredictionDialog';

const STAGE_CONFIG = {
  draft: { label: 'Draft', color: '#94A3B8', bg: '#F1F5F9', icon: FileText, desc: 'Created, pending submission' },
  submitted: { label: 'Submitted', color: '#1A56DB', bg: '#1A56DB/10', icon: Send, desc: 'Sent to clearinghouse' },
  in_review: { label: 'In Review', color: '#92400E', bg: '#92400E/10', icon: Eye, desc: 'Under payer review' },
  adjudicated: { label: 'Adjudicated', color: '#7C3AED', bg: '#7C3AED/10', icon: Shield, desc: 'Decision rendered' },
  paid: { label: 'Paid', color: '#166534', bg: '#166534/10', icon: CheckCircle, desc: 'Payment received' },
  denied: { label: 'Denied', color: '#991B1B', bg: '#991B1B/10', icon: XCircle, desc: 'Claim rejected' },
  appealed: { label: 'Appealed', color: '#0E7490', bg: '#0E7490/10', icon: RotateCcw, desc: 'Under appeal review' },
};

const PIPELINE_ORDER = ['draft', 'submitted', 'in_review', 'adjudicated', 'paid'];

export default function LiveClaimsTrackerPage() {
  const [trackerData, setTrackerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillStage, setDrillStage] = useState(null);
  const [stageClaims, setStageClaims] = useState([]);
  const [stageLoading, setStageLoading] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [claimDetail, setClaimDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [drillWidget, setDrillWidget] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [predictionClaim, setPredictionClaim] = useState(null);

  const loadTracker = useCallback(async () => {
    try {
      const res = await claimApi.tracker();
      setTrackerData(res.data);
    } catch (err) {
      console.error('Failed to load tracker:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTracker(); }, [loadTracker]);

  // Auto-refresh every 15s
  useEffect(() => {
    const interval = setInterval(loadTracker, 15000);
    return () => clearInterval(interval);
  }, [loadTracker]);

  const drillIntoStage = async (stage) => {
    setDrillStage(stage);
    setStageLoading(true);
    try {
      const res = await claimApi.pipeline(stage);
      setStageClaims(res.data.claims || []);
    } catch (err) {
      toast.error('Failed to load claims');
    } finally {
      setStageLoading(false);
    }
  };

  const openClaimDetail = async (claimId) => {
    setSelectedClaim(claimId);
    setDetailLoading(true);
    try {
      const res = await claimApi.detail(claimId);
      setClaimDetail(res.data);
    } catch (err) {
      toast.error('Failed to load claim details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDrillWidget = async (widget) => {
    setDrillWidget(widget);
    if (widget === 'denied') {
      await drillIntoStage('denied');
    } else if (widget === 'paid') {
      await drillIntoStage('paid');
    } else if (widget === 'pending') {
      setStageLoading(true);
      try {
        const stages = ['submitted', 'in_review', 'adjudicated'];
        const all = [];
        for (const s of stages) {
          const res = await claimApi.pipeline(s);
          all.push(...(res.data.claims || []));
        }
        setStageClaims(all);
        setDrillStage('pending');
      } catch (err) {
        toast.error('Failed to load');
      } finally {
        setStageLoading(false);
      }
    } else if (widget === 'total') {
      setStageLoading(true);
      try {
        const res = await claimApi.list();
        setStageClaims((res.data.claims || []).map(c => ({
          ...c, tracking: [], procedure_descriptions: [], procedure_codes: c.procedure_codes || [], diagnosis_codes: c.diagnosis_codes || []
        })));
        setDrillStage('all');
      } catch (err) {
        toast.error('Failed to load');
      } finally {
        setStageLoading(false);
      }
    }
  };

  const seedDemoClaims = async () => {
    setSeeding(true);
    try {
      const res = await claimApi.seedDemo();
      toast.success(res.data.message);
      await loadTracker();
    } catch (err) {
      toast.error('Failed to seed claims');
    } finally {
      setSeeding(false);
    }
  };

  const closeDrill = () => {
    setDrillStage(null);
    setStageClaims([]);
    setDrillWidget(null);
  };

  if (loading) return <div className="flex justify-center py-16"><div className="w-10 h-10 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" /></div>;

  const s = trackerData?.summary || {};
  const p = trackerData?.pipeline || {};
  const isEmpty = s.total_claims === 0;

  const stageLabel = drillStage === 'pending' ? 'Pending Claims' : drillStage === 'all' ? 'All Claims' : STAGE_CONFIG[drillStage]?.label || '';

  return (
    <div className="space-y-6" data-testid="live-claims-tracker">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Live Claims Tracker
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <div className="w-2 h-2 rounded-full bg-[#166534] animate-pulse" />
            <span className="text-sm text-[#64748B]">Auto-refreshes every 15s</span>
          </div>
        </div>
        <div className="flex gap-2">
          {isEmpty && (
            <Button onClick={seedDemoClaims} disabled={seeding} variant="outline" data-testid="seed-claims-btn">
              <Database className="w-4 h-4 mr-2" /> {seeding ? 'Seeding...' : 'Load Demo Claims'}
            </Button>
          )}
          <Button variant="outline" onClick={() => { setLoading(true); loadTracker(); }} data-testid="refresh-tracker">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {isEmpty ? (
        <Card className="careiq-card">
          <CardContent className="p-12 text-center">
            <Activity className="w-16 h-16 mx-auto mb-4 text-[#94A3B8]" />
            <h3 className="text-xl font-semibold text-[#0A2342]">No Claims Yet</h3>
            <p className="text-[#64748B] mt-2 max-w-md mx-auto">Start by loading demo claims to see the live pipeline tracker in action.</p>
            <Button className="mt-4 bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" onClick={seedDemoClaims} disabled={seeding} data-testid="seed-claims-btn-empty">
              <Zap className="w-4 h-4 mr-2" /> {seeding ? 'Creating Claims...' : 'Generate 40 Demo Claims'}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* KPI Widgets — All Clickable */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="careiq-card cursor-pointer hover:shadow-lg hover:border-[#0A2342]/30 transition-all group" onClick={() => handleDrillWidget('total')} data-testid="widget-total">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-[#64748B] flex items-center gap-1"><FileText className="w-3 h-3" /> Total Claims</div>
                    <div className="text-2xl font-bold text-[#0A2342] mt-1">{s.total_claims}</div>
                    <div className="text-xs text-[#0E7490] mt-1">${s.total_billed?.toLocaleString()} billed</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[#94A3B8] group-hover:text-[#0A2342] transition-colors" />
                </div>
              </CardContent>
            </Card>
            <Card className="careiq-card cursor-pointer hover:shadow-lg hover:border-[#92400E]/30 transition-all group" onClick={() => handleDrillWidget('pending')} data-testid="widget-pending">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-[#64748B] flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</div>
                    <div className="text-2xl font-bold text-[#92400E] mt-1">{(p.submitted?.count || 0) + (p.in_review?.count || 0) + (p.adjudicated?.count || 0)}</div>
                    <div className="text-xs text-[#64748B] mt-1">Awaiting resolution</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[#94A3B8] group-hover:text-[#92400E] transition-colors" />
                </div>
              </CardContent>
            </Card>
            <Card className="careiq-card cursor-pointer hover:shadow-lg hover:border-[#166534]/30 transition-all group" onClick={() => handleDrillWidget('paid')} data-testid="widget-recovery">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-[#64748B] flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Recovery Rate</div>
                    <div className="text-2xl font-bold text-[#166534] mt-1">{s.recovery_rate}%</div>
                    <div className="text-xs text-[#166534] mt-1">${s.total_paid?.toLocaleString()} collected</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[#94A3B8] group-hover:text-[#166534] transition-colors" />
                </div>
              </CardContent>
            </Card>
            <Card className="careiq-card cursor-pointer hover:shadow-lg hover:border-[#991B1B]/30 transition-all group" onClick={() => handleDrillWidget('denied')} data-testid="widget-denials">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-[#64748B] flex items-center gap-1"><XCircle className="w-3 h-3" /> Denial Rate</div>
                    <div className="text-2xl font-bold text-[#991B1B] mt-1">{s.denial_rate}%</div>
                    <div className="text-xs text-[#991B1B] mt-1">{p.denied?.count || 0} denied</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[#94A3B8] group-hover:text-[#991B1B] transition-colors" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Pipeline Visualization — Uber-style */}
          <Card className="careiq-card overflow-hidden" data-testid="pipeline-visualization">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
                <Activity className="w-5 h-5 text-[#0E7490]" /> Claims Pipeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Main pipeline flow */}
              <div className="flex items-center justify-between gap-1 overflow-x-auto pb-4">
                {PIPELINE_ORDER.map((stage, idx) => {
                  const cfg = STAGE_CONFIG[stage];
                  const Icon = cfg.icon;
                  const data = p[stage] || { count: 0, total_amount: 0 };
                  return (
                    <React.Fragment key={stage}>
                      <div
                        className="flex-1 min-w-[140px] p-4 rounded-xl cursor-pointer border-2 transition-all hover:shadow-lg hover:scale-[1.02]"
                        style={{ borderColor: cfg.color + '40', background: `${cfg.color}08` }}
                        onClick={() => drillIntoStage(stage)}
                        data-testid={`pipeline-${stage}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: cfg.color + '20' }}>
                            <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                          </div>
                          <span className="text-xs font-medium" style={{ color: cfg.color }}>{cfg.label}</span>
                        </div>
                        <div className="text-2xl font-bold text-[#0A2342]">{data.count}</div>
                        <div className="text-xs text-[#64748B]">${data.total_amount?.toLocaleString()}</div>
                        <div className="text-[10px] text-[#94A3B8] mt-1">{cfg.desc}</div>
                      </div>
                      {idx < PIPELINE_ORDER.length - 1 && (
                        <div className="flex-shrink-0 flex items-center">
                          <div className="w-6 h-0.5 bg-[#E2E8F0]" />
                          <ArrowRight className="w-4 h-4 text-[#94A3B8] -mx-1" />
                          <div className="w-6 h-0.5 bg-[#E2E8F0]" />
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Secondary: Denied + Appealed */}
              <div className="flex gap-3 mt-2 pt-3 border-t border-[#E2E8F0]">
                {['denied', 'appealed'].map(stage => {
                  const cfg = STAGE_CONFIG[stage];
                  const Icon = cfg.icon;
                  const data = p[stage] || { count: 0, total_amount: 0 };
                  return (
                    <div
                      key={stage}
                      className="flex-1 p-3 rounded-lg cursor-pointer border transition-all hover:shadow-md"
                      style={{ borderColor: cfg.color + '40', background: `${cfg.color}08` }}
                      onClick={() => drillIntoStage(stage)}
                      data-testid={`pipeline-${stage}`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                        <span className="text-sm font-medium" style={{ color: cfg.color }}>{cfg.label}</span>
                        <span className="ml-auto text-lg font-bold text-[#0A2342]">{data.count}</span>
                      </div>
                      <div className="text-xs text-[#64748B] mt-0.5">${data.total_amount?.toLocaleString()}</div>
                    </div>
                  );
                })}
                <div className="flex-1 p-3 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC]">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#0E7490]" />
                    <span className="text-sm font-medium text-[#0E7490]">Avg. Days to Pay</span>
                    <span className="ml-auto text-lg font-bold text-[#0A2342]">{s.avg_days_to_payment || 0}</span>
                  </div>
                  <div className="text-xs text-[#64748B] mt-0.5">Industry avg: 30-45 days</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="careiq-card" data-testid="recent-activity">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#0A2342]">Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(trackerData?.recent_activity || []).map((claim) => {
                  const cfg = STAGE_CONFIG[claim.status] || STAGE_CONFIG.draft;
                  const Icon = cfg.icon;
                  return (
                    <div
                      key={claim.id}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#F8FAFC] cursor-pointer transition-colors group"
                      onClick={() => openClaimDetail(claim.id)}
                      data-testid={`activity-${claim.id}`}
                    >
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: cfg.color + '15' }}>
                        <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-[#0A2342] font-mono">{claim.claim_number}</span>
                          <span className="px-2 py-0.5 text-[10px] rounded-full font-medium" style={{ backgroundColor: cfg.color + '15', color: cfg.color }}>{cfg.label}</span>
                        </div>
                        <div className="text-xs text-[#64748B] truncate">
                          {claim.patient_name} &middot; {claim.insurance_provider} &middot; {claim.clearinghouse}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-semibold text-sm text-[#0A2342]">${claim.total_amount?.toLocaleString()}</div>
                        <div className="text-[10px] text-[#64748B]">{claim.service_date}</div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-[#94A3B8] flex-shrink-0 group-hover:text-[#0A2342]" />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Drill-down: Stage Claims List */}
      <Dialog open={!!drillStage} onOpenChange={() => closeDrill()}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh]" aria-describedby="drill-stage-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {drillStage && STAGE_CONFIG[drillStage] && (() => { const Icon = STAGE_CONFIG[drillStage].icon; return <Icon className="w-5 h-5" style={{ color: STAGE_CONFIG[drillStage].color }} />; })()}
              {stageLabel} ({stageClaims.length})
            </DialogTitle>
            <p id="drill-stage-desc" className="text-sm text-[#64748B]">Click any claim to view full tracking details</p>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            {stageLoading ? (
              <div className="flex justify-center py-8"><div className="w-8 h-8 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" /></div>
            ) : stageClaims.length > 0 ? (
              <div className="space-y-2 pr-2">
                {stageClaims.map(claim => {
                  const cfg = STAGE_CONFIG[claim.status] || STAGE_CONFIG.draft;
                  return (
                    <div
                      key={claim.id}
                      className="flex items-center gap-3 p-3 rounded-lg border border-[#E2E8F0] hover:border-[#0E7490] hover:bg-[#F8FAFC] cursor-pointer transition-all"
                      onClick={() => openClaimDetail(claim.id)}
                      data-testid={`drill-claim-${claim.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm text-[#0A2342] font-mono">{claim.claim_number}</span>
                          <span className="px-2 py-0.5 text-[10px] rounded-full font-medium" style={{ backgroundColor: cfg.color + '15', color: cfg.color }}>{cfg.label}</span>
                        </div>
                        <div className="text-xs text-[#64748B] mt-0.5">
                          {claim.patient_name} &middot; {claim.insurance_provider}
                        </div>
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {(claim.procedure_descriptions || claim.procedure_codes || []).slice(0, 2).map((p, i) => (
                            <span key={i} className="text-[10px] px-1.5 py-0.5 bg-[#F1F5F9] text-[#64748B] rounded">{p}</span>
                          ))}
                        </div>
                        {claim.denial_reason && <div className="text-[10px] text-[#991B1B] mt-1">Reason: {claim.denial_reason}</div>}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-semibold text-[#0A2342]">${claim.total_amount?.toLocaleString()}</div>
                        {claim.payment_amount && <div className="text-xs text-[#166534]">Paid: ${claim.payment_amount?.toLocaleString()}</div>}
                        <div className="text-[10px] text-[#64748B]">{claim.service_date}</div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-[#94A3B8] flex-shrink-0" />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-[#64748B]">No claims in this stage</div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Claim Detail with Uber-style Timeline */}
      <Dialog open={!!selectedClaim} onOpenChange={() => { setSelectedClaim(null); setClaimDetail(null); }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh]" aria-describedby="claim-detail-desc">
          {detailLoading ? (
            <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" /></div>
          ) : claimDetail ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-mono">{claimDetail.claim_number}</DialogTitle>
                <p id="claim-detail-desc" className="text-sm text-[#64748B]">Full claim tracking details</p>
              </DialogHeader>
              <ScrollArea className="max-h-[65vh]">
                <div className="space-y-4 pr-2">
                  {/* Status Badge */}
                  <div className="flex items-center gap-3">
                    {(() => {
                      const cfg = STAGE_CONFIG[claimDetail.status];
                      const Icon = cfg?.icon || FileText;
                      return (
                        <span className="px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5" style={{ backgroundColor: (cfg?.color || '#94A3B8') + '15', color: cfg?.color || '#94A3B8' }}>
                          <Icon className="w-4 h-4" /> {cfg?.label || claimDetail.status}
                        </span>
                      );
                    })()}
                    <span className="text-lg font-bold text-[#0A2342]">${claimDetail.total_amount?.toLocaleString()}</span>
                    {claimDetail.payment_amount && (
                      <span className="text-sm text-[#166534] font-medium">Paid: ${claimDetail.payment_amount?.toLocaleString()}</span>
                    )}
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-[#F8FAFC] rounded-lg">
                      <div className="text-[10px] text-[#64748B]">Patient</div>
                      <div className="text-sm font-medium text-[#0A2342] mt-0.5">{claimDetail.patient_name}</div>
                    </div>
                    <div className="p-3 bg-[#F8FAFC] rounded-lg">
                      <div className="text-[10px] text-[#64748B]">Insurance</div>
                      <div className="text-sm font-medium text-[#0A2342] mt-0.5">{claimDetail.insurance_provider}</div>
                    </div>
                    <div className="p-3 bg-[#F8FAFC] rounded-lg">
                      <div className="text-[10px] text-[#64748B]">Service Date</div>
                      <div className="text-sm font-medium text-[#0A2342] mt-0.5">{claimDetail.service_date}</div>
                    </div>
                    <div className="p-3 bg-[#F8FAFC] rounded-lg">
                      <div className="text-[10px] text-[#64748B]">Clearinghouse</div>
                      <div className="text-sm font-medium text-[#0A2342] mt-0.5">{claimDetail.clearinghouse}</div>
                    </div>
                  </div>

                  {/* Codes */}
                  <div className="space-y-2">
                    <div className="text-xs text-[#64748B] font-medium">Diagnosis Codes</div>
                    <div className="flex gap-1 flex-wrap">
                      {(claimDetail.diagnosis_codes || []).map((c, i) => (
                        <span key={i} className="px-2 py-0.5 text-xs bg-[#991B1B]/10 text-[#991B1B] rounded font-mono">{c}</span>
                      ))}
                    </div>
                    <div className="text-xs text-[#64748B] font-medium mt-2">Procedures</div>
                    <div className="space-y-1">
                      {(claimDetail.procedure_descriptions || claimDetail.procedure_codes || []).map((p, i) => (
                        <div key={i} className="text-xs px-2 py-1 bg-[#0E7490]/10 text-[#0E7490] rounded">{p}</div>
                      ))}
                    </div>
                  </div>

                  {claimDetail.denial_reason && (
                    <div className="p-3 bg-[#991B1B]/5 border-l-4 border-[#991B1B] rounded-r-lg">
                      <div className="text-xs text-[#991B1B] font-medium">Denial Reason</div>
                      <div className="text-sm text-[#0A2342] mt-0.5">{claimDetail.denial_reason}</div>
                    </div>
                  )}

                  {/* AI Denial Prediction Button */}
                  {['draft', 'submitted', 'in_review'].includes(claimDetail.status) && (
                    <Button
                      variant="outline"
                      className="w-full text-[#0E7490] border-[#0E7490]/30 hover:bg-[#0E7490]/5"
                      onClick={() => { setPredictionClaim(claimDetail); }}
                      data-testid="tracker-predict-denial-btn"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Run AI Denial Risk Analysis
                    </Button>
                  )}

                  {/* Uber-style Tracking Timeline */}
                  <div>
                    <div className="text-sm font-semibold text-[#0A2342] mb-3">Tracking Timeline</div>
                    <div className="space-y-0">
                      {(claimDetail.tracking || []).map((event, i) => {
                        const evtCfg = STAGE_CONFIG[event.stage] || STAGE_CONFIG.draft;
                        const isLast = i === claimDetail.tracking.length - 1;
                        return (
                          <div key={i} className="flex gap-3" data-testid={`timeline-event-${i}`}>
                            <div className="flex flex-col items-center">
                              <div className="w-3 h-3 rounded-full border-2 flex-shrink-0" style={{ borderColor: evtCfg.color, backgroundColor: isLast ? evtCfg.color : 'white' }} />
                              {!isLast && <div className="w-0.5 h-full min-h-[32px]" style={{ backgroundColor: evtCfg.color + '30' }} />}
                            </div>
                            <div className="pb-4 -mt-0.5">
                              <div className="text-xs font-medium" style={{ color: evtCfg.color }}>{evtCfg.label}</div>
                              <div className="text-[11px] text-[#64748B]">{event.note}</div>
                              <div className="text-[10px] text-[#94A3B8] mt-0.5">
                                {event.timestamp ? new Date(event.timestamp).toLocaleString() : ''}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </ScrollArea>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* AI Denial Prediction Dialog */}
      <DenialPredictionDialog
        open={!!predictionClaim}
        onClose={() => setPredictionClaim(null)}
        claim={predictionClaim}
      />
    </div>
  );
}
